import React, { useRef, useState } from "react";
import axios from "axios";
import Card from "../Card";
import "../AppUser/styles.css";

export type Airline = {
    id: string | number;
    name: string;
    code: string;
    sapCode: string | null;
    email: string | null;
};

interface AirlineCardProps {
    airline?: Airline;
    onSave: () => void;
    onCancel: () => void;
}

const AirlineCard: React.FC<AirlineCardProps> = ({ airline, onSave, onCancel }) => {
    const [name, setName] = useState(airline?.name ?? "");
    const [code, setCode] = useState(airline?.code ?? "");
    const [sapCode, setSapCode] = useState(airline?.sapCode ?? "");
    const [email, setEmail] = useState(airline?.email ?? "");
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (savingRef.current) return;
        if (!name.trim() || !code.trim()) {
            setError("Vyplňte název a kód společnosti.");
            return;
        }
        savingRef.current = true;
        setSaving(true);
        setError(null);
        try {
            await axios.post("api/airline/save", {
                id: airline?.id,
                name: name.trim(),
                code: code.trim(),
                sapCode: sapCode.trim(),
                email: email.trim()
            });
            onSave();
        } catch (err) {
            setError(axios.isAxiosError(err) && typeof err.response?.data?.message === "string"
                ? err.response.data.message
                : "Leteckou společnost se nepodařilo uložit. Zkuste to prosím znovu.");
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return (
        <Card title={airline ? "Upravit leteckou společnost" : "Nová letecká společnost"}
            suppressInit displaySelectedStock={false}>
            <form onSubmit={save}>
                <fieldset className="airline-fields" disabled={saving}>
                    <div className="nec-stack">
                        <div className="nec-block">
                            <label className="nec-header" htmlFor="airline-name">Název *</label>
                            <div className="nec-control">
                                <input id="airline-name" className="nec-input" value={name} required autoFocus
                                    onChange={event => setName(event.target.value)} />
                            </div>
                        </div>
                        <div className="nec-block">
                            <label className="nec-header" htmlFor="airline-code">Kód společnosti *</label>
                            <div className="nec-control">
                                <input id="airline-code" className="nec-input" value={code} required
                                    onChange={event => setCode(event.target.value)} />
                            </div>
                        </div>
                        <div className="nec-block">
                            <label className="nec-header" htmlFor="airline-sap">SAP kód</label>
                            <div className="nec-control">
                                <input id="airline-sap" className="nec-input" value={sapCode}
                                    onChange={event => setSapCode(event.target.value)} />
                            </div>
                        </div>
                        <div className="nec-block">
                            <label className="nec-header" htmlFor="airline-email">E-mail</label>
                            <div className="nec-control">
                                <input id="airline-email" className="nec-input" type="email" value={email}
                                    onChange={event => setEmail(event.target.value)} />
                            </div>
                        </div>
                        {error && <div className="form-error" role="alert">{error}</div>}
                    </div>
                    <div className="floating-actions">
                        <button className="floating-action-button" type="button" onClick={onCancel}>Zrušit</button>
                        <button className="floating-action-button" type="submit">{saving ? "Ukládání…" : "Uložit"}</button>
                    </div>
                </fieldset>
            </form>
        </Card>
    );
};

export default AirlineCard;
